import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, inject, Sidebar as SidebarFrame, SidebarLink } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import displayIcon from '../../../Mri.Web.Common/Assets/Icons/display-icon.svg';
import analyticsIcon from '../../../Mri.Web.Common/Assets/Icons/analytics-icon.png';
import boldvuIcon from '../../../Mri.Web.Common/Assets/boldvu.png';
import collapsedIcon from '../../../Mri.Web.Common/Assets/MRI-favicon-light.png';
import mountainIcon from '../../../Mri.Web.Common/Assets/polygons-1.png';
import { AccountStore } from '../Account/AccountStore';
import { DisplayEventProfileViews } from '../DisplayEventProfiles/DisplayEventProfilesList';
import { AppStore } from './AppStore';
import { BsColumns } from '@react-icons/all-files/bs/BsColumns';
import { BsBuilding } from '@react-icons/all-files/bs/BsBuilding';
import { BsCalendar } from '@react-icons/all-files/bs/BsCalendar';
import { BsClipboard } from '@react-icons/all-files/bs/BsClipboard';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';
import { BsEye } from '@react-icons/all-files/bs/BsEye';
import { BsFileEarmarkZip } from '@react-icons/all-files/bs/BsFileEarmarkZip';
import { BsGrid3X3GapFill } from '@react-icons/all-files/bs/BsGrid3X3GapFill';
import { BsLightning } from '@react-icons/all-files/bs/BsLightning';
import { BsHouse } from '@react-icons/all-files/bs/BsHouse';
import { BsTag } from '@react-icons/all-files/bs/BsTag';
import { BsPersonWorkspace } from 'react-icons/bs';

export interface ISidebarProps extends RouteComponentProps<undefined> {
    readonly closeSidebar: any;
    readonly collapseSidebar: any;
    readonly sidebarVisible: boolean;
    readonly sidebarCollapsed: boolean;
}

@autoinject
@observer
export class Sidebar extends React.Component<ISidebarProps> {
    public render() {
        return (
            <SidebarFrame
                title={this.sidebarTitle}
                visible={this.props.sidebarVisible}
                className={this.collapsedClass}
                links={this.renderLinks}
                footer={this.renderFooter}
                collapseSidebar={this.props.collapseSidebar}
                sidebarCollapsed={this.props.sidebarCollapsed}
                isMedium={this.appStore.isMedium}
            />
        );
    }

    @computed
    get renderLinks() {
        const isInternalUser = this.accountStore.isInRoles(['MriAdmin', 'MriUser']);

        return (
            <ul>
                {!isInternalUser && (
                    <SidebarLink
                        customIcon={<BsHouse className="nav-icon" />}
                        label="Home"
                        exact
                        url="/"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<img className="nav-icon img-fluid inc-width" src={displayIcon} />}
                        label="Displays"
                        url="/displays"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsCalendar className="nav-icon img-fluid inc-width" />}
                        label="Display Event Profiles"
                        url="/display-event-profiles"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        <SidebarLink
                            label="Automations"
                            url={`/display-event-profiles/${DisplayEventProfileViews.Automations}`}
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />

                        <SidebarLink
                            label="Alerts"
                            url={`/display-event-profiles/${DisplayEventProfileViews.Alerts}`}
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />
                    </SidebarLink>
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsExclamationTriangleFill className="nav-icon img-fluid inc-width" />}
                        label="Warranty Alerts"
                        url="/warranty-alerts"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsGrid3X3GapFill className="nav-icon img-fluid inc-width" />}
                        label="Display Groups"
                        url="/display-groups"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsEye className="nav-icon img-fluid inc-width" />}
                        label="Watchlist"
                        url="/favorites"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser &&
                    this.accountStore.isInRoles(['MriAdmin', 'MriCustomerServiceAdmin', 'MriCustomerServiceRep']) && (
                    <SidebarLink
                        customIcon={<BsPersonWorkspace className="nav-icon img-fluid inc-width" />}
                        label="Customer Service Dashboard"
                        url="/customer-service-dashboard"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        {isInternalUser && this.accountStore.isInRoles(['MriAdmin', 'MriCustomerServiceAdmin']) && (
                            <SidebarLink
                                label="Admin"
                                url="/customer-service-dashboard/admin"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}
                        <SidebarLink
                            label="Dashboard"
                            url="/customer-service-dashboard"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />
                    </SidebarLink>
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsTag className="nav-icon img-fluid inc-width" />}
                        label="Tickets"
                        url="/tickets"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsBuilding className="nav-icon img-fluid inc-width" />}
                        label="Accounts"
                        url="/accounts"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsColumns className="nav-icon img-fluid inc-width" />}
                        label="Warehouses"
                        url="/warehouses"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {/*
                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsBell className='nav-icon' />}
                        label="Warranty Alerts"
                        url="/display-alerts"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}
                */}

                {this.accountStore.isInRoles(['MriAdmin', 'ProdUser']) && (
                    <SidebarLink
                        customIcon={<BsClipboard className="nav-icon img-fluid inc-width" />}
                        label="Production Projects"
                        url="/production-projects"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        {isInternalUser && (
                            <SidebarLink
                                label="Projects"
                                url="/production-projects/projects"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}

                        {isInternalUser && (
                            <SidebarLink
                                label="Model Definitions"
                                url="/model-definitions/models"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}

                        {isInternalUser && (
                            <SidebarLink
                                label="Part Definitions"
                                url="/part-definitions"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}
                        {isInternalUser && (
                            <SidebarLink
                                label="Service Kits"
                                url="/service-kits"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}
                    </SidebarLink>
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'ProdTestUser']) && (
                    <SidebarLink
                        customIcon={<BsLightning className="nav-icon img-fluid inc-width" />}
                        label="Software Updates"
                        url="/software-updates"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        {isInternalUser && (
                            <SidebarLink
                                label="Manifests"
                                url="/software-updates/manifests"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}

                        {isInternalUser && (
                            <SidebarLink
                                label="Configs"
                                url="/cloud-configs/configs"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child={true}
                            />
                        )}

                        <SidebarLink
                            label="File Systems"
                            url="/software-updates/file-systems"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />
                    </SidebarLink>
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<BsFileEarmarkZip className="nav-icon img-fluid inc-width" />}
                        label="Data Management"
                        url="/csv"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        <SidebarLink
                            label="Import Displays"
                            url="/csv/import"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />

                        <SidebarLink
                            label="Export Displays"
                            url="/csv/export"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />
                    </SidebarLink>
                )}

                {isInternalUser && (
                    <SidebarLink
                        customIcon={<img className="nav-icon img-fluid inc-width2" src={analyticsIcon} />}
                        label="Business Intelligence"
                        url="/bi"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        <SidebarLink
                            label="Dashboards"
                            url="/bi/dashboards"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />

                        <SidebarLink
                            label="Reports"
                            url="/bi/reports"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />
                    </SidebarLink>
                )}
            </ul>
        );
    }

    @computed
    get renderFooter() {
        return (
            <div className="sidebar-polygon">
                <img className="collapsed-polygon" src={mountainIcon} />
            </div>
        );
    }

    @computed
    get collapsedClass() {
        if (this.props.sidebarCollapsed && !this.appStore.isMobile) {
            return ' collapsed-sidebar';
        }
        return '';
    }

    @computed
    get sidebarTitle() {
        return (
            <LinkContainer to="/" className="w-100 text-center">
                <a className="">
                    {(!this.appStore.storage.sidebarCollapsed || this.appStore.isMedium) && (
                        <img className="img-fluid p-6" src={boldvuIcon} />
                    )}

                    {this.appStore.storage.sidebarCollapsed && !this.appStore.isMedium && (
                        <img className="img-fluid" src={collapsedIcon} />
                    )}
                </a>
            </LinkContainer>
        );
    }

    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
}
